import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Check, CircleCheck, Circle, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { BUTTON, INTRO, NOTE_EXAMPLE, NOTE_LABEL, checklistRows, missingLine, readyLine } from "../../lib/portalPreparation";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { fieldCls, primaryBtn, secondaryBtn } from "./PortalShell";

/**
 * "Pregătirea grupei" (15.10.2026): what the trainer has ready before the
 * participants are invited, and the button that tells the team. Pressed
 * again, before the invitations, it carries a new note.
 */
export default function GroupPreparation({ groupId, prep, onChanged }) {
  const [note, setNote] = useState("");
  const [again, setAgain] = useState(false);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const { hash } = useLocation();

  // The to-do line links here.
  useEffect(() => {
    if (hash !== "#pregatirea-grupei") return undefined;
    const t = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(t);
  }, [hash]);

  const rows = checklistRows(prep);
  const gaps = missingLine(prep);
  const showForm = !prep.ready_at || again;

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/groups/${groupId}/ready`, { note });
      toast.success("Am anunțat echipa.");
      setAsking(false);
      setAgain(false);
      setNote("");
      onChanged?.();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut anunța echipa. Încearcă din nou."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section ref={ref} id="pregatirea-grupei" className="mt-4 scroll-mt-4 rounded-2xl border border-[#d99a2b]/40 bg-[#d99a2b]/[0.07] px-4 py-4"
      data-testid="portal-preparation">
      <h2 className="font-display text-lg font-bold text-[#373A45]">Pregătirea grupei</h2>
      <p className="mt-1 text-sm text-[#5c4a1f]">{INTRO}</p>

      <ul className="mt-3 grid gap-1.5 text-sm text-[#373A45]">
        {rows.map((r) => (
          <li key={r.key} className="flex items-center gap-2">
            {r.info ? <CalendarClock size={16} className="shrink-0 text-[#6B7280]" aria-hidden="true" />
              : r.done ? <CircleCheck size={16} className="shrink-0 text-[#519872]" aria-label="gata" />
                : <Circle size={16} className="shrink-0 text-[#9CA3AF]" aria-label="încă nu" />}
            <span>{r.label}</span>
          </li>
        ))}
      </ul>

      {prep.ready_at && (
        <p className="mt-3 flex items-start gap-2 text-sm font-semibold text-[#3c7457]" data-testid="portal-preparation-ready">
          <Check size={16} className="mt-0.5 shrink-0" aria-hidden="true" /> {readyLine(prep.ready_at)}
        </p>
      )}
      {prep.ready_at && prep.ready_note && <p className="ml-6 mt-1 text-xs text-[#6B7280]">Nota ta: „{prep.ready_note}”</p>}
      {prep.ready_at && !again && (
        <button type="button" onClick={() => setAgain(true)} data-testid="portal-preparation-again"
          className="ml-6 mt-2 text-sm font-semibold text-[#7A4986] hover:text-[#965EA7]">
          Anunță din nou
        </button>
      )}

      {showForm && (
        <div className="mt-4 grid gap-2">
          <label className="grid gap-1 text-sm text-[#373A45]">
            <span className="font-semibold">{NOTE_LABEL}</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500}
              placeholder={NOTE_EXAMPLE} className={fieldCls} data-testid="portal-preparation-note" />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} className={primaryBtn} data-testid="portal-preparation-send"
              onClick={() => (gaps ? setAsking(true) : send())}>
              {busy && <Loader2 size={15} className="mr-1.5 inline animate-spin" />}{BUTTON}
            </button>
            {again && (
              <button type="button" disabled={busy} className={secondaryBtn} onClick={() => { setAgain(false); setNote(""); }}>
                Renunță
              </button>
            )}
          </div>
        </div>
      )}

      <Dialog open={asking} onOpenChange={(v) => { if (!v && !busy) setAsking(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anunți echipa?</DialogTitle>
            <DialogDescription>{gaps}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button type="button" disabled={busy} className={secondaryBtn} onClick={() => setAsking(false)}>Renunță</button>
            <button type="button" disabled={busy} className={primaryBtn} onClick={send} data-testid="portal-preparation-confirm">
              {busy && <Loader2 size={15} className="mr-1.5 inline animate-spin" />}Anunță echipa
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
